import React, { useState } from 'react';
import { CreditCard, ExternalLink, Calendar, AlertCircle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../../../utils/api';
import SeatManagementPanel from '../../common/SeatManagementPanel';

const BillingSettings = () => {
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: subsData, isLoading } = useQuery({
    queryKey: ['billing', 'subscriptions'],
    queryFn: async () => {
      const res = await apiGet('/billing/subscriptions');
      return res.ok ? res.json() : { subscriptions: [], exempt: false };
    },
  });

  const subscriptions = subsData?.subscriptions || [];
  const exempt = subsData?.exempt || false;

  const cancelMutation = useMutation({
    mutationFn: async (subId) => {
      const res = await apiPost('/billing/cancel', { subscription_id: subId });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to cancel');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscriptions'] });
    },
    onError: (err) => setError(err.message),
  });

  const handlePortal = async () => {
    try {
      setPortalLoading(true);
      const response = await apiPost('/billing/portal', {
        billing_type: currentUser?.clientMembership ? 'org' : 'individual',
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to open billing portal');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancel = (subId) => {
    if (!window.confirm('Cancel this subscription? You will retain access until the end of the current period.')) return;
    cancelMutation.mutate(subId);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (exempt) {
    return (
      <div className="space-y-4">
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
          <p className="text-green-700 dark:text-green-400 font-rasa">
            Your role grants you full access to all platform features. No subscription required.
          </p>
        </div>
      </div>
    );
  }

  const activeSubs = subscriptions.filter(s => s.active_access);

  // A sub is "owned" by the current user when they pay or manage it:
  //   - Individual subs (org_subscription === false) belong to the user.
  //   - Org subs are owned only when the current user is client_manager of
  //     that specific client_id. Plain members get access but no management.
  const isClientManagerOf = (clientId) =>
    (currentUser?.clientMemberships || []).some(
      cm => cm.client_id === clientId && cm.client_role === 'client_manager'
    );
  const isOwnedSub = (sub) => !sub.org_subscription || isClientManagerOf(sub.client_id);

  const ownedSubs = activeSubs.filter(isOwnedSub);
  const accessOnlySubs = activeSubs.filter(s => !isOwnedSub(s));
  const hasPaidSub = ownedSubs.some(s => s.source === 'stripe');

  const renderSubCard = (sub) => (
    <div key={sub.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <CreditCard size={18} className="text-blue-500" />
            <h4 className="font-unna font-semibold dark:text-white">{sub.plan_name}</h4>
            <span className={`text-xs px-2 py-0.5 rounded-full font-rasa ${
              sub.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
              sub.status === 'trialing' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
              'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
            }`}>
              {sub.status}
            </span>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400 font-rasa space-y-1">
            <p>
              {sub.subscriber_label || (sub.org_subscription ? sub.client_name : 'Individual')}
              {' '}&middot;{' '}
              {sub.source === 'stripe' ? sub.billing_interval : sub.source?.replace('_', ' ')}
            </p>
            {sub.current_period_end && (
              <p className="flex items-center gap-1">
                <Calendar size={14} /> Renews {formatDate(sub.current_period_end)}
              </p>
            )}
            {sub.cancel_at && (
              <p className="text-orange-500 flex items-center gap-1">
                <AlertCircle size={14} /> Canceling on {formatDate(sub.cancel_at)}
              </p>
            )}
          </div>
        </div>
        {sub.source === 'stripe' && sub.status !== 'canceled' && !sub.cancel_at && (
          <button onClick={() => handleCancel(sub.id)} className="text-sm text-red-500 hover:text-red-600 font-rasa">
            Cancel
          </button>
        )}
      </div>
      {(sub.org_subscription || sub.seat_count > 1) && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <SeatManagementPanel subscriptionId={sub.id} />
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Your access — read-only summary of every sub that grants the
          current user features. Includes plans they own AND plans they
          access through an org membership. Just a quick "what do I have?". */}
      {activeSubs.length > 0 && (
        <div>
          <h3 className="text-sm font-rasa font-semibold text-gray-500 dark:text-gray-400 mb-2">Your access</h3>
          <div className="flex flex-wrap gap-2">
            {activeSubs.map(sub => {
              const owned = isOwnedSub(sub);
              return (
                <span
                  key={sub.id}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-rasa ${
                    owned
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                  title={owned ? 'You own this plan' : `Provided by ${sub.client_name || 'your organization'}`}
                >
                  {sub.plan_name}
                  <span className="text-xs opacity-70">
                    {owned ? '(yours)' : `via ${sub.client_name || 'org'}`}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Plans you own — the management surface. Only subs the current
          user pays for or administers; plain org-membership access doesn't
          appear here because there's nothing to manage. */}
      <div>
        <h3 className="text-sm font-rasa font-semibold text-gray-500 dark:text-gray-400 mb-2">Plans you own</h3>
        {ownedSubs.length > 0 ? (
          <div className="space-y-3">
            {ownedSubs.map(sub => renderSubCard(sub))}
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 text-center">
            <p className="text-gray-500 dark:text-gray-400 font-rasa mb-3">
              {accessOnlySubs.length > 0
                ? "You don't own any plans yet. Subscribe to a plan you can manage and assign seats from."
                : 'No active subscriptions'}
            </p>
            <button
              onClick={() => navigate('/dashboard/upgrade')}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-rasa"
            >
              View Plans
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        {hasPaidSub && (
          <button
            onClick={handlePortal}
            disabled={portalLoading}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-rasa dark:text-gray-300 disabled:opacity-50"
          >
            <ExternalLink size={16} />
            {portalLoading ? 'Opening...' : 'Manage Payment & Invoices'}
          </button>
        )}
        <button
          onClick={() => navigate('/dashboard/upgrade')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-rasa"
        >
          {ownedSubs.length > 0 ? 'Change Plan' : 'View Plans'}
        </button>
      </div>
    </div>
  );
};

export default BillingSettings;
